import {Transition, TransitionChild} from "@headlessui/react";
import {token} from "@atlaskit/tokens";
import CrossIcon from "@atlaskit/icon/core/cross";
import type {PointerEvent as ReactPointerEvent, ReactNode} from "react";
import {useEffect, useLayoutEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {ResizableBox} from "react-resizable";
import {twMerge} from "tailwind-merge";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";

export function BottomDrawer({
    open,
    setOpen,
    children,
    title,
    zIndex,
    /** Opening height in px (clamped to max drawer height). Defaults to 300. Ignored when `fitContentOnOpen` is true. */
    initialOpenHeight,
    /** When true, opening height fits scrollable content (capped at max). User can still drag to expand. */
    fitContentOnOpen = false,
    /** Rendered outside the scrollable area, always visible at the bottom of the drawer. */
    footer,
    fullScreen = false,
    /**
     * When set with `fullScreen`, the sheet and backdrop start below this many pixels from the top of the viewport
     * (e.g. workspace header height) so the header stays visible. Z-index defaults below fixed headers (z-[100]).
     */
    fullScreenTopOffsetPx,
    /** When true, draws a border under the title row (e.g. mobile action sheets). */
    headerBottomBorder = false,
    /** When true, draws a border above the footer. */
    footerTopBorder = false,
    /**
     * Omit the drawer's title row and close control (e.g. when using the fixed app header and dismiss via backdrop).
     */
    hideHeader = false,
    /** When true, body does not scroll; children manage their own overflow (e.g. split-pane filter drawers). */
    lockContentScroll = false,
    /** When true, sheet height hugs header + content (no stretched empty middle). Use with `fitContentOnOpen`. */
    compactLayout = false,
}: {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    children: ReactNode;
    title?: ReactNode | string;
    zIndex?: number;
    initialOpenHeight?: number;
    fitContentOnOpen?: boolean;
    footer?: ReactNode;
    fullScreen?: boolean;
    fullScreenTopOffsetPx?: number;
    headerBottomBorder?: boolean;
    footerTopBorder?: boolean;
    hideHeader?: boolean;
    lockContentScroll?: boolean;
    compactLayout?: boolean;
}) {
    const [height, setHeight] = useState<number>(300);
    const [maxHeight, setMaxHeight] = useState<number>(0);
    const minHeight = 0.15;
    const headerMeasureRef = useRef<HTMLDivElement>(null);
    const contentMeasureRef = useRef<HTMLDivElement>(null);
    const footerMeasureRef = useRef<HTMLDivElement>(null);
    const headerDragStateRef = useRef<{
        pointerId: number;
        startY: number;
        startHeight: number;
    } | null>(null);

    const fullScreenTopOffsetPxResolved =
        fullScreen === true && typeof fullScreenTopOffsetPx === "number" && fullScreenTopOffsetPx > 0 ? fullScreenTopOffsetPx : 0;
    const fullScreenBelowHeader = fullScreen === true && fullScreenTopOffsetPxResolved > 0;

    const viewportHeightMinusTopOffset = () => {
        if (typeof window === "undefined") {
            return 800;
        }
        return Math.max(240, window.innerHeight - fullScreenTopOffsetPxResolved);
    };

    useEffect(() => {
        if (typeof window !== "undefined") {
            setMaxHeight(fullScreen ? viewportHeightMinusTopOffset() : window.innerHeight * 0.9);
        }

        const handleResize = () => {
            if (typeof window !== "undefined") {
                setMaxHeight(fullScreen ? viewportHeightMinusTopOffset() : window.innerHeight * 0.9);
                setHeight((prevHeight) => {
                    if (fullScreen === true) {
                        return viewportHeightMinusTopOffset();
                    }
                    return Math.min(prevHeight, window.innerHeight * 0.9);
                });
            }
        };

        if (typeof window !== "undefined") {
            window.addEventListener("resize", handleResize);
            return () => {
                window.removeEventListener("resize", handleResize);
            };
        }
    }, [fullScreen, fullScreenTopOffsetPxResolved]);

    useEffect(() => {
        if (open !== true || typeof window === "undefined") {
            return;
        }
        if (fullScreen === true) {
            setHeight(viewportHeightMinusTopOffset());
            return;
        }
        if (fitContentOnOpen === true) {
            return;
        }
        const cap = window.innerHeight * 0.9;
        const base = initialOpenHeight ?? 300;
        setHeight(Math.min(Math.max(base, 0), cap));
    }, [open, initialOpenHeight, fitContentOnOpen, fullScreen, fullScreenTopOffsetPxResolved]);

    useLayoutEffect(() => {
        if (open !== true || fitContentOnOpen !== true || typeof window === "undefined") {
            return;
        }
        const cap =
            compactLayout === true
                ? Math.min(window.innerHeight * 0.55, window.innerHeight * 0.9)
                : window.innerHeight * 0.9;
        const paddingTopPx = 24;
        const paddingBottomPx = 40;
        const gapPx = compactLayout === true ? 0 : 8;
        const measure = () => {
            const headerEl = headerMeasureRef.current;
            const contentEl = contentMeasureRef.current;
            if (headerEl === null || contentEl === null) {
                return;
            }
            const headerH = headerEl.offsetHeight;
            const contentH = contentEl.scrollHeight;
            const footerEl = footerMeasureRef.current;
            const footerH = footerEl !== null ? footerEl.offsetHeight : 0;
            const footerGap = footerH > 0 ? gapPx : 0;
            const total = paddingTopPx + headerH + gapPx + contentH + footerGap + footerH + paddingBottomPx;
            const minHeightPx = compactLayout === true ? 180 : 260;
            setHeight(Math.min(Math.max(total, minHeightPx), cap));
        };
        measure();
        const raf = requestAnimationFrame(measure);
        const ro = new ResizeObserver(measure);
        const el = contentMeasureRef.current;
        if (el !== null) {
            ro.observe(el);
        }
        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
        };
    }, [open, fitContentOnOpen, children, compactLayout]);

    useEffect(() => {
        if (typeof window !== "undefined") {
            if (height < minHeight * window.innerHeight) {
                setOpen(false);
            }
        }
    }, [height, setOpen]);

    const gridRowsClass =
        compactLayout === true
            ? footer !== undefined
                ? "grid-rows-[max-content_max-content_max-content]"
                : "grid-rows-[max-content_max-content]"
            : footer !== undefined
              ? "grid-rows-[max-content_minmax(0,1fr)_max-content]"
              : "grid-rows-[max-content_minmax(0,1fr)]";

    if (open === false) {
        return null;
    }

    /**
     * Edge-to-edge full-screen stacks above the page. When `fullScreenTopOffsetPx` is set, stack below fixed
     * headers (e.g. z-[100]) so the header stays visible and interactive.
     */
    const sheetStackZ = zIndex ?? (fullScreen === true ? (fullScreenBelowHeader === true ? 90 : 200) : 60);
    const blanketStackZ = sheetStackZ - 1;

    const fullScreenLayerHeightPx = typeof window !== "undefined" ? viewportHeightMinusTopOffset() : 800;

    function getMaxSheetHeightPx(): number {
        if (typeof window === "undefined") {
            return 800;
        }
        if (fullScreen === true) {
            return fullScreenLayerHeightPx;
        }
        if (maxHeight > 0) {
            return maxHeight;
        }
        return window.innerHeight * 0.9;
    }

    function getMinSheetHeightPx(): number {
        if (typeof window === "undefined") {
            return 120;
        }
        return minHeight * window.innerHeight;
    }

    function handleHeaderPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
        if (fullScreen === true) {
            return;
        }
        if (event.pointerType === "mouse" && event.button !== 0) {
            return;
        }
        const target = event.target;
        if (target instanceof HTMLElement && target.closest("button, a, input, textarea, select, [role='button'], [role='link']") !== null) {
            return;
        }

        headerDragStateRef.current = {
            pointerId: event.pointerId,
            startY: event.clientY,
            startHeight: height,
        };

        const previousUserSelect = document.body.style.userSelect;
        document.body.style.userSelect = "none";

        const onPointerMove = (moveEvent: PointerEvent) => {
            const drag = headerDragStateRef.current;
            if (drag === null || moveEvent.pointerId !== drag.pointerId) {
                return;
            }
            const deltaY = drag.startY - moveEvent.clientY;
            const nextHeight = drag.startHeight + deltaY;
            const clampedHeight = Math.min(Math.max(nextHeight, getMinSheetHeightPx()), getMaxSheetHeightPx());
            setHeight(clampedHeight);
        };

        const stopDragging = (stopEvent: PointerEvent) => {
            const drag = headerDragStateRef.current;
            if (drag === null || stopEvent.pointerId !== drag.pointerId) {
                return;
            }
            headerDragStateRef.current = null;
            document.body.style.userSelect = previousUserSelect;
            window.removeEventListener("pointermove", onPointerMove);
            window.removeEventListener("pointerup", stopDragging);
            window.removeEventListener("pointercancel", stopDragging);
        };

        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", stopDragging);
        window.addEventListener("pointercancel", stopDragging);
    }

    return (
        <>
            {createPortal(
                <Transition
                    as="div"
                    show={open}
                    transition={true}
                >
                    <TransitionChild as="div">
                        <button
                            type="button"
                            onClick={() => {
                                setOpen(false);
                            }}
                            className={twMerge(
                                "fixed duration-200 data-[closed]:opacity-0",
                                fullScreen === true ? "opacity-100" : "opacity-50",
                                fullScreenBelowHeader === true ? "left-0 right-0 bottom-0 w-full" : "inset-0 w-full h-full",
                            )}
                            style={{
                                backgroundColor: fullScreen === true ? token("elevation.surface", "#FFFFFF") : token("color.blanket"),
                                zIndex: blanketStackZ,
                                ...(fullScreenBelowHeader === true ? {top: fullScreenTopOffsetPxResolved} : {}),
                            }}
                        />
                    </TransitionChild>
                    <TransitionChild
                        as="div"
                        transition={true}
                    >
                        <div
                            className={twMerge(
                                "w-full transition-all duration-200 ease-in",
                                fullScreen === true && fullScreenBelowHeader === true
                                    ? "fixed left-0 right-0 bottom-0 overflow-hidden"
                                    : fullScreen === true
                                      ? "fixed inset-0 h-[100dvh] max-h-[100dvh] overflow-hidden"
                                      : "fixed bottom-0 left-0 z-[60]",
                            )}
                            style={{
                                zIndex: fullScreen === true ? sheetStackZ : (zIndex ?? 60),
                                ...(fullScreenBelowHeader === true ? {top: fullScreenTopOffsetPxResolved} : {}),
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                        >
                            <ResizableBox
                                width={Infinity}
                                height={height}
                                handleSize={[0, 0]}
                                lockAspectRatio={false}
                                transformScale={1}
                                minConstraints={fullScreen === true ? [Infinity, fullScreenLayerHeightPx] : [Infinity, minHeight]}
                                maxConstraints={[
                                    Infinity,
                                    fullScreen === true
                                        ? fullScreenLayerHeightPx
                                        : maxHeight > 0
                                          ? maxHeight
                                          : typeof window !== "undefined"
                                            ? window.innerHeight * 0.9
                                            : 800,
                                ]}
                                axis="y"
                                onResizeStop={(_e: unknown, data: {size: {height: number}}) => {
                                    if (fullScreen === true) {
                                        return;
                                    }
                                    const newHeight = data.size.height;
                                    if (typeof newHeight === "number") {
                                        setHeight(newHeight);
                                    }
                                }}
                                resizeHandles={fullScreen === true ? [] : ["n"]}
                                handle={
                                    fullScreen === true ? (
                                        <div />
                                    ) : (
                                        <div
                                            data-direction="n"
                                            className="absolute top-0 left-0 w-full h-10 flex items-center justify-center cursor-n-resize z-50"
                                        >
                                            <button
                                                type="button"
                                                className="w-full h-full flex items-center justify-center z-10"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (typeof window !== "undefined") {
                                                        const imgHeight = height === window.innerHeight * 0.9 ? 300 : window.innerHeight * 0.9;
                                                        setHeight(imgHeight);
                                                    }
                                                }}
                                            >
                                                <div
                                                    className="w-12 h-1.5 rounded-full"
                                                    style={{
                                                        backgroundColor: token("color.border.bold", "#44546F"),
                                                    }}
                                                />
                                            </button>
                                        </div>
                                    )
                                }
                                className={twMerge(
                                    "w-full",
                                    fullScreen === true &&
                                        (fullScreenBelowHeader === true ? "h-full min-h-0 max-h-full !h-full" : "!h-[100dvh] min-h-[100dvh] max-h-[100dvh]"),
                                )}
                            >
                                <div
                                    className={twMerge(
                                        "h-full w-full overflow-hidden grid px-0",
                                        fullScreen === true ? "pt-0 gap-y-ds-space-100" : "pt-6",
                                        lockContentScroll === true || compactLayout === true ? "gap-y-0" : "gap-2",
                                        gridRowsClass,
                                        fullScreen === true ? "" : "rounded-t-3xl",
                                    )}
                                    style={{
                                        backgroundColor: token("elevation.surface"),
                                        paddingBottom: "calc(1rem + env(safe-area-inset-bottom))",
                                        paddingTop:
                                            fullScreen === true
                                                ? fullScreenBelowHeader === true
                                                    ? "0.5rem"
                                                    : "calc(env(safe-area-inset-top, 0px) + 0.5rem)"
                                                : undefined,
                                    }}
                                >
                                    <div
                                        ref={headerMeasureRef}
                                        className={twMerge("w-full min-w-0 flex flex-col", hideHeader === true ? "min-h-0" : "")}
                                    >
                                        {hideHeader !== true ? (
                                            <>
                                                <div
                                                    className={twMerge(
                                                        "relative grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-ds-space-100 px-4",
                                                        fullScreen === true ? "pb-ds-space-100" : "z-[60] pb-ds-space-150",
                                                    )}
                                                    onPointerDown={handleHeaderPointerDown}
                                                    style={fullScreen === true ? undefined : {cursor: "ns-resize"}}
                                                >
                                                    {typeof title === "string" ? (
                                                        <div
                                                            className="text-[1.25rem] leading-ds-space-300 font-ds-font-weight-bold"
                                                            style={{color: token("color.text"), fontFamily: "Inter"}}
                                                        >
                                                            {title}
                                                        </div>
                                                    ) : (
                                                        <div className="min-w-0 flex-1">{title}</div>
                                                    )}
                                                    <AnankeIconButton
                                                        type="button"
                                                        appearance="subtle"
                                                        icon={CrossIcon}
                                                        label=""
                                                        onClick={() => {
                                                            setOpen(false);
                                                        }}
                                                    />
                                                </div>
                                                {fullScreen === true || headerBottomBorder === true ? (
                                                    <div
                                                        aria-hidden
                                                        className="w-full shrink-0"
                                                        style={{
                                                            height: 1,
                                                            backgroundColor: token("color.border", "#091E4224"),
                                                        }}
                                                    />
                                                ) : null}
                                            </>
                                        ) : null}
                                    </div>
                                    <div
                                        className={twMerge(
                                            "min-h-0 min-w-0",
                                            compactLayout === true ? "h-auto" : "h-full",
                                            lockContentScroll === true ? "overflow-hidden px-0" : "overflow-y-auto overscroll-y-contain touch-pan-y px-4",
                                        )}
                                    >
                                        <div
                                            ref={contentMeasureRef}
                                            className={twMerge("w-full min-w-0", compactLayout === true ? "h-auto" : "min-h-0 h-full")}
                                        >
                                            {children}
                                        </div>
                                    </div>
                                    {footer !== undefined ? (
                                        <div
                                            className={twMerge(
                                                "w-full min-w-0 shrink-0",
                                                fullScreen === true || footerTopBorder === true ? "border-t border-solid" : "",
                                            )}
                                            style={
                                                fullScreen === true || footerTopBorder === true
                                                    ? {
                                                          borderTop: `1px solid ${token("color.border", "#091E4224")}`,
                                                      }
                                                    : undefined
                                            }
                                        >
                                            <div
                                                ref={footerMeasureRef}
                                                className={twMerge("px-4 w-full", fullScreen === true ? "pt-ds-space-150 mt-ds-space-050" : "pt-ds-space-100")}
                                            >
                                                {footer}
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            </ResizableBox>
                        </div>
                    </TransitionChild>
                </Transition>,
                document.body,
            )}
        </>
    );
}
